import {
  CanvasTextMetrics,
  Container,
  Graphics,
  Text,
  TextStyle,
  type TextStyleFontWeight,
} from 'pixi.js';
import { FONT_ROLE } from '../../theme';
import { DESIGN_HEIGHT, DESIGN_WIDTH, dashPolyline, flatten, type Pt } from './geom';
import type { FaceSpec, Paint, Prim, Stroke } from './spec';
import { BASELINE_RATIO } from './tune';

/**
 * **牌面 IR → Pixi。全项目唯一一份"怎么画"的实现。**
 *
 * 它刻意很哑：只认 6 种原语，不知道什么是"半调""笔迹""勒索信"
 * （那些在生成阶段就已经展开成多边形了）。所以这个文件不会因为
 * 加一张新牌而改动 —— 新牌只加在 `faces/` 里。
 *
 * ## 合并策略
 *
 * Pixi 的 `Graphics` **按插入顺序执行绘制指令**，所以连续的简单图元
 * （多边形 / 圆 / 矩形 / 线）可以塞进**同一个** Graphics 而不改变层叠顺序。
 * 画法里半调网点动辄几百个圆，一个一个建 Graphics 会很重。
 *
 * 碰到 `text` / `group` 就 flush —— 那两种没法合进 Graphics。
 *
 * ## 缩放
 *
 * 画法写的是**设计空间 100 × 140**。这里按 `width / DESIGN_WIDTH` 等比缩放。
 * 比例由 `metrics.ts` 保证统一，所以 `height / DESIGN_HEIGHT` 必然等于同一个数。
 */

export interface FaceSize {
  readonly width: number;
  readonly height: number;
}

/**
 * **裁到牌框内的牌面** —— 表格 / 缩略图 / 抽牌堆这种"牌彼此紧挨"的场合用。
 *
 * ⚠️ `paintFace` 刻意**保留出血**（图形可以伸出牌框），那是**手牌**那种
 * "牌浮在桌面上、四周有洞"的场合要的。缩略图里没有洞，出血就会糊到隔壁 ——
 * 所以这里给**同一份画法**套一个矩形遮罩，而不是另写一套画法。
 */
export function paintClippedFace(spec: FaceSpec, size: FaceSize): Container {
  const root = new Container();
  root.label = 'card-face-clipped';
  const mask = new Graphics().rect(0, 0, size.width, size.height).fill({ color: 0xffffff });
  const face = paintFace(spec, size);
  root.addChild(mask, face);
  face.mask = mask;
  return root;
}

export function paintFace(spec: FaceSpec, size: FaceSize): Container {
  // ⚠️ 缩放系数按 **spec 自己声明的设计空间**算，不是死按 100。
  //    牌背和标题 Logo 走的也是这条管线（见 `spec.ts` 的 `design`）——
  //    它们和牌面共用 painter，才不会又分叉出第二套画法。
  const design = spec.design ?? { width: DESIGN_WIDTH, height: DESIGN_HEIGHT };
  const s = size.width / design.width;
  const root = new Container();
  root.label = 'card-face';

  if (spec.ground.t === 'solid') {
    const g = new Graphics();
    const r = (spec.groundRadius ?? 0) * s;
    if (r > 0) g.roundRect(0, 0, size.width, size.height, r);
    else g.rect(0, 0, size.width, size.height);
    g.fill(paintStyle(spec.ground));
    root.addChild(g);
  }

  // ② **出血层。画在底色之后、内容之前，而且不裁。**
  //
  // ⚠️ 顺序是这里唯一要紧的事。底色是**不透明**的：
  //    - 出血层画在底色**之前** → 牌内那部分被底色盖掉，出框的图形就看不见了
  //    - 画在底色**之后** → 牌内牌外都对，而且**一份几何就够**，
  //      作者不用在 `prims` 和 `spill` 里各写一遍
  //
  //    所以「出框」的统一写法就是：**把会伸出去的图形放进 `spill`**，
  //    它自己会在牌内外都正确显示。
  if (spec.spill !== undefined && spec.spill.length > 0) {
    const spill = paintPrims(spec.spill, s);
    spill.label = 'card-spill';
    root.addChild(spill);
  }

  // ③ 内容层：**一律裁到牌内**。
  //
  // ⚠️ 这条不是可选的。牌面纹理比牌大一圈（含出血），所以牌自己的墨
  //    也会漏进出血区 —— 异常牌那两个圆心在牌外的大圆，会在牌右边
  //    挂出一段孤零零的弧；`抽到此牌者` 那条画到 x=118 的表格线也会探出去。
  //    在打样里它们被卡牌边界切掉了，这里必须显式切，否则出血一开就露馅。
  //
  //    判据很简单：**`prims` 是长在牌上的，`spill` 才是伸到牌外的。**
  //    想让一条线出去，就把它放进 `spill` —— 那是要作者明确声明的动作。
  const content = paintPrims(spec.prims, s);
  // ③ 内容层（裁到牌内）与最上面那圈外框
  const mask = cardRectMask(spec, size, s);
  // ⚠️ 遮罩**必须进显示列表**，否则 Pixi 不会把它渲染进遮罩缓冲，
  //    结果是内容被胡乱裁掉一块（踩过：牌的整个下半截凭空消失，而报错是干净的）。
  //    被指为 mask 的对象不会再当内容画一遍，所以这里不会多出一个白块
  root.addChild(mask);
  content.mask = mask;
  root.addChild(content);

  // ④ 外框最后画 —— 它是牌的边，不该被内容压住
  if (spec.border !== undefined) {
    const g = new Graphics();
    const w = Math.max(0.05, spec.border.width * s);
    // 内描边：把线画在边界里侧，免得被 FBO 裁掉一半
    g.rect(w / 2, w / 2, size.width - w, size.height - w);
    g.stroke(strokeStyle(spec.border, s));
    root.addChild(g);
  }

  return root;
}

/** 牌本身的那块矩形，用来把内容层裁在牌内 */
function cardRectMask(spec: FaceSpec, size: FaceSize, s: number): Graphics {
  const mask = new Graphics();
  const r = (spec.groundRadius ?? 0) * s;
  if (r > 0) mask.roundRect(0, 0, size.width, size.height, r);
  else mask.rect(0, 0, size.width, size.height);
  mask.fill(0xffffff);
  return mask;
}

// ============================================================
// 图元
// ============================================================

function paintPrims(prims: readonly Prim[], s: number): Container {
  const out = new Container();
  let g: Graphics | null = null;

  const flush = (): void => {
    if (g !== null) {
      out.addChild(g);
      g = null;
    }
  };

  for (const p of prims) {
    if (p.k === 'text' || p.k === 'group') {
      flush();
      out.addChild(p.k === 'text' ? paintText(p, s) : paintGroup(p, s));
      continue;
    }
    g ??= new Graphics();
    drawSimple(g, p, s);
  }
  flush();
  return out;
}

function drawSimple(
  g: Graphics,
  p: Extract<Prim, { k: 'poly' | 'line' | 'circle' | 'rect' }>,
  s: number,
): void {
  switch (p.k) {
    case 'poly': {
      if (p.pts.length < 3) return;
      g.poly(scalePts(p.pts, s));
      applyPaint(g, p.fill, p.stroke, s);
      return;
    }
    case 'circle': {
      g.circle(p.x * s, p.y * s, Math.max(0.01, p.r * s));
      applyPaint(g, p.fill, p.stroke, s);
      return;
    }
    case 'rect': {
      g.rect(p.x * s, p.y * s, p.w * s, p.h * s);
      applyPaint(g, p.fill, p.stroke, s);
      return;
    }
    case 'line': {
      // ⚠️ Pixi 的 Graphics 没有 dash —— 虚线是**在几何层切好**的（`geom.dashPolyline`）。
      // 切出来的是实心小段，所以线宽、端点样式都照常生效
      const segs = p.stroke.dash === undefined ? [p.pts] : dashPolyline(p.pts, p.stroke.dash);
      let drew = false;
      for (const seg of segs) {
        if (seg.length < 2) continue;
        g.moveTo(seg[0]!.x * s, seg[0]!.y * s);
        for (let i = 1; i < seg.length; i++) g.lineTo(seg[i]!.x * s, seg[i]!.y * s);
        drew = true;
      }
      if (drew) g.stroke(strokeStyle(p.stroke, s));
      return;
    }
  }
}

function applyPaint(
  g: Graphics,
  fill: Paint | undefined,
  stroke: Stroke | undefined,
  s: number,
): void {
  if (fill !== undefined && fill.t === 'solid') g.fill(paintStyle(fill));
  if (stroke !== undefined) g.stroke(strokeStyle(stroke, s));
}

function paintStyle(p: Paint): { color: number; alpha: number } {
  return p.t === 'solid' ? { color: p.color, alpha: p.alpha } : { color: 0x000000, alpha: 0 };
}

function strokeStyle(st: Stroke, s: number): {
  width: number;
  color: number;
  alpha?: number;
  cap: 'butt' | 'round';
  join: 'miter' | 'round';
} {
  return {
    width: Math.max(0.05, st.width * s),
    color: st.color,
    ...(st.alpha === undefined ? {} : { alpha: st.alpha }),
    cap: st.cap ?? 'butt',
    join: st.join ?? 'miter',
  };
}

function scalePts(pts: readonly Pt[], s: number): number[] {
  const flat = flatten(pts);
  for (let i = 0; i < flat.length; i++) flat[i] = flat[i]! * s;
  return flat;
}

// ============================================================
// 文字
// ============================================================

function paintText(p: Extract<Prim, { k: 'text' }>, s: number): Container {
  const size = p.size * s;
  const style = new TextStyle({
    fontFamily: FONT_ROLE[p.role],
    fontSize: size,
    fill: p.color,
    fontWeight: String(p.weight ?? 400) as TextStyleFontWeight,
    letterSpacing: (p.tracking ?? 0) * s,
    align: 'left',
    ...(p.lineHeight === undefined ? {} : { lineHeight: p.lineHeight * s }),
  });

  const node = new Text({
    text: p.wrap === undefined ? p.s : wrapByChars(p.s, p.wrap),
    style,
  });
  node.anchor.set(anchorX(p.anchor), 0);

  // ⚠️ 齐宽**必须量**：没有字体文件，`FONT_DISPLAY` 在不同机器上落到不同字体，
  //    宽度各不相同，猜一个字号一定会有的机器上超出、有的机器上短一截。
  //    只缩 x —— 字形被横向压扁，而"紧、扁、贴边"正是这套标题要的样子。
  if (p.fitWidth !== undefined) {
    const natural = node.width;
    if (natural > 0) node.scale.x = (p.fitWidth * s) / natural;
  }

  // ⚠️ 画法里的 y 是**基线**（和 SVG 一致），而 Pixi 的锚点对着**包围盒顶部**，
  // 所以要往上减掉"顶到基线"的那一截。
  //
  // ⚠️ 这一截**必须量，不能猜**。第一版拍了个常数 0.78，结果中文的 ascent
  // 实际接近 0.88 —— 「异常」整个沉下去，正好顶到下面那行 EXCEPTION。
  // 而且两种字体的偏差不一样，一个常数不可能同时对。
  const dy = -baselineOffset(style, p.size * s);

  if (p.rotate === undefined || p.rotate === 0) {
    node.position.set(p.x * s, p.y * s + dy);
    return node;
  }
  const holder = new Container();
  holder.position.set(p.x * s, p.y * s);
  holder.rotation = (p.rotate * Math.PI) / 180;
  node.position.set(0, dy);
  holder.addChild(node);
  return holder;
}

/**
 * 「文字块顶部 → 第一行基线」的距离（像素）。
 *
 * ⚠️ **必须量，不能猜。** 第一版拍了个常数 0.78，结果中文字体的 ascent
 * 接近 0.88 —— 「异常」整个沉下去，正好顶到下面那行 `EXCEPTION`。
 * 而且中文和等宽字体的偏差不一样，一个常数不可能同时撑对。
 *
 * ⚠️ 公式抄的是 Pixi **自己的排版模型**（`CanvasTextGenerator.mjs`）：
 *
 * ```js
 * baseline(j) = ascent + (lineHeight - fontSize) / 2 + j * lineHeight
 * ```
 *
 * 注意它用的是 `fontSize` 而不是 `ascent + descent` —— 这一条不看源码
 * 是猜不出来的。用的是 Pixi 的度量，就得用 Pixi 的算法，否则两边迟早对不上。
 */
const baselineCache = new Map<string, number>();

function baselineOffset(style: TextStyle, fallbackSize: number): number {
  const key = [
    String(style.fontFamily),
    String(style.fontSize),
    String(style.fontWeight),
    style.lineHeight === null || style.lineHeight === undefined ? '' : String(style.lineHeight),
  ].join('|');

  const hit = baselineCache.get(key);
  if (hit !== undefined) return hit;

  let value: number;
  try {
    const m = CanvasTextMetrics.measureText('M', style);
    const fp = m.fontProperties;
    value =
      fp.fontSize > 0
        ? fp.ascent + (m.lineHeight - fp.fontSize) / 2
        : fallbackSize * BASELINE_RATIO;
  } catch {
    // 字体还没加载 / 没有 canvas —— 退回常数，总比把文字画到牌外面强
    value = fallbackSize * BASELINE_RATIO;
  }
  baselineCache.set(key, value);
  return value;
}

function paintGroup(p: Extract<Prim, { k: 'group' }>, s: number): Container {
  const c = new Container();
  if (p.at !== undefined) c.position.set(p.at.x * s, p.at.y * s);
  // ⚠️ pivot 是**组内部**的坐标，rotate/scale 绕它。设了它才能"子图元保持绝对坐标、
  //    只绕某点转一下"（`at` 和 `pivot` 都给同一个点）。见 `spec.ts` 里那张表
  if (p.pivot !== undefined) c.pivot.set(p.pivot.x * s, p.pivot.y * s);
  if (p.rotate !== undefined) c.rotation = (p.rotate * Math.PI) / 180;
  if (p.scale !== undefined) c.scale.set(p.scale);
  if (p.alpha !== undefined) c.alpha = p.alpha;
  c.addChild(paintPrims(p.prims, s));
  return c;
}

function anchorX(a: 'start' | 'middle' | 'end' | undefined): number {
  return a === 'middle' ? 0.5 : a === 'end' ? 1 : 0;
}

/**
 * 按**字符数**折行。
 *
 * ⚠️ 中文没有词边界，`wordWrap` 那种按空格断行的做法对中文完全无效 ——
 * 只会在每段末尾断一次。所以画法直接给"每行几个字"。
 */
export function wrapByChars(s: string, perLine: number): string {
  if (perLine <= 0) return s;
  const lines: string[] = [];
  for (const raw of s.split('\n')) {
    let line = '';
    for (const ch of raw) {
      line += ch;
      if (line.length >= perLine) {
        lines.push(line);
        line = '';
      }
    }
    if (line !== '') lines.push(line);
  }
  return lines.join('\n');
}
